import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip,
  Checkbox, FormControlLabel, CircularProgress, Alert, TextField, Autocomplete,
  Divider, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';

const BulkAssignDialog = ({ open, onClose, classes, streams, subjects, onSubmit, loading }) => {
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [settings, setSettings] = useState({ is_compulsory: true, pass_mark: 50, max_marks: 100, weight_percentage: 100 });

  // For each selected class, get its streams (if any)
  const classStreamMap = useMemo(() => {
    const map = {};
    selectedClasses.forEach(cls => {
      map[cls.id] = streams.filter(s => s.class_id === cls.id);
    });
    return map;
  }, [selectedClasses, streams]);

  // All papers from all selected subjects
  const allPapers = useMemo(() =>
    selectedSubjects.flatMap(subj => (subj.papers || []).map(p => ({ ...p, subjectName: subj.subject_name }))),
    [selectedSubjects]
  );

  // Preview: total assignments to be created
  const totalAssignments = useMemo(() => {
    let count = 0;
    selectedClasses.forEach(cls => {
      const clsStreams = classStreamMap[cls.id] || [];
      const targets = clsStreams.length > 0 ? clsStreams.length : 1;
      count += targets * allPapers.length;
    });
    return count;
  }, [selectedClasses, classStreamMap, allPapers]);

  const handleSubmit = () => {
    if (selectedClasses.length === 0) { alert('Select at least one class'); return; }
    if (selectedSubjects.length === 0) { alert('Select at least one subject'); return; }
    if (allPapers.length === 0) { alert('Selected subjects have no papers'); return; }

    const assignments = [];
    selectedClasses.forEach(cls => {
      const clsStreams = classStreamMap[cls.id] || [];
      allPapers.forEach(paper => {
        if (clsStreams.length === 0) {
          assignments.push({ class_id: cls.id, stream_id: null, subject_paper_id: paper.id, ...settings });
        } else {
          clsStreams.forEach(cs => {
            assignments.push({ class_id: cls.id, stream_id: cs.stream_id, subject_paper_id: paper.id, ...settings });
          });
        }
      });
    });

    onSubmit(assignments);
  };

  const handleClose = () => {
    setSelectedClasses([]);
    setSelectedSubjects([]);
    setSettings({ is_compulsory: true, pass_mark: 50, max_marks: 100, weight_percentage: 100 });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Assign Subject Papers</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select multiple classes and subjects. All papers of each subject will be assigned to all streams of each class.
          </Alert>

          {/* Classes */}
          <Autocomplete
            multiple
            fullWidth
            options={classes}
            value={selectedClasses}
            onChange={(_, value) => setSelectedClasses(value)}
            getOptionLabel={(option) => option.name || option.class_name || 'Class'}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} margin="normal" label="Classes" required />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip key={option.id} label={option.name || option.class_name} size="small" {...getTagProps({ index })} />
              ))
            }
          />

          {/* Show streams per selected class */}
          {selectedClasses.length > 0 && (
            <Box sx={{ mt: 1, mb: 1 }}>
              {selectedClasses.map(cls => {
                const clsStreams = classStreamMap[cls.id] || [];
                return (
                  <Box key={cls.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                      {cls.name || cls.class_name}:
                    </Typography>
                    {clsStreams.length === 0 ? (
                      <Chip label="No streams (class-level)" size="small" color="warning" variant="outlined" />
                    ) : (
                      clsStreams.map(cs => {
                        const stream = streams.find(s => s.id === cs.stream_id);
                        return <Chip key={cs.stream_id} label={stream?.name || 'Stream'} size="small" color="primary" variant="outlined" icon={<CheckCircle />} />;
                      })
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Subjects */}
          <Autocomplete
            multiple
            fullWidth
            options={subjects}
            value={selectedSubjects}
            onChange={(_, value) => setSelectedSubjects(value)}
            getOptionLabel={(option) => `${option.subject_name || 'Subject'} (${option.papers?.length || 0} papers)`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} margin="normal" label="Subjects" required />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip key={option.id} label={`${option.subject_name} (${option.papers?.length || 0})`} size="small" {...getTagProps({ index })} />
              ))
            }
          />

          {/* Papers preview */}
          {allPapers.length > 0 && (
            <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 180, overflowY: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>Papers to assign ({allPapers.length})</Typography>
              <List dense disablePadding>
                {allPapers.map(paper => (
                  <ListItem key={paper.id} disableGutters>
                    <ListItemIcon sx={{ minWidth: 28 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText primary={paper.paper_name} secondary={`${paper.subjectName} · ${paper.paper_code}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>Assignment Settings</Typography>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <FormControlLabel
              control={<Checkbox checked={settings.is_compulsory} onChange={(e) => setSettings({ ...settings, is_compulsory: e.target.checked })} />}
              label="Compulsory"
            />
            <Autocomplete
              size="small" sx={{ minWidth: 130 }}
              options={[30, 40, 50, 60]}
              value={settings.pass_mark}
              onChange={(_, v) => setSettings({ ...settings, pass_mark: v ?? 50 })}
              renderInput={(params) => <TextField {...params} label="Pass Mark" />}
            />
            <Autocomplete
              size="small" sx={{ minWidth: 130 }}
              options={[50, 100, 150, 200]}
              value={settings.max_marks}
              onChange={(_, v) => setSettings({ ...settings, max_marks: v ?? 100 })}
              renderInput={(params) => <TextField {...params} label="Max Marks" />}
            />
            <Autocomplete
              size="small" sx={{ minWidth: 130 }}
              options={[25, 50, 75, 100]}
              value={settings.weight_percentage}
              onChange={(_, v) => setSettings({ ...settings, weight_percentage: v ?? 100 })}
              renderInput={(params) => <TextField {...params} label="Weight %" />}
            />
          </Box>

          {totalAssignments > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <strong>{totalAssignments} assignments</strong> will be created across {selectedClasses.length} class(es) and {allPapers.length} paper(s)
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedClasses.length || !allPapers.length || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Assigning...' : `Create ${totalAssignments} Assignments`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkAssignDialog;
